/**
 * @component SignOutButton
 * @author Akhilesh Singh
 * @description
 * The `SignOutButton` component renders a dropdown menu that provides user profile information
 * and a logout option. It utilizes Reactstrap's `Dropdown` components for the dropdown functionality
 * and integrates with the `useAuth` hook to access user data and handle the logout process.
 * @returns {JSX.Element} The rendered dropdown menu with user profile information and logout option.
 */

import React, { useState } from "react";
import "../../Header/header.scss";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import EmailIcon from "../../../assets/Images/Email.png";
import ProfileImg from "../../../assets/Images/profile1.png";
import { useAuth } from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import "./SignOutButton.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/Reducer";

/**
 * @remarks
 * - The component uses the `useState` hook to manage the dropdown's open/close state.
 * - The `useAuth` hook is used to access user information and the logout function.
 * - The `toggleHandler` function toggles the dropdown's open/close state.
 * - The component returns a dropdown menu with user profile information and a logout option.
 */

export const SignOutButton: React.FC<{}> = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toggleHandler = () => setIsDropdownOpen(!isDropdownOpen);
  const profileData = useSelector(
    (state: RootState) => state.profile.profileData
  );

  return (
    <Dropdown isOpen={isDropdownOpen} toggle={toggleHandler}>
      <DropdownToggle>
        <div className="profileText">My Profile</div>
        <div className="ProfileName">
          {user?.name
            .split(" ")
            .map((letter) => letter[0])
            .join("")}
        </div>
      </DropdownToggle>
      <DropdownMenu>
        <div className="profile-dropdown-content">
          <div className="profile-image-container">
            <img src={ProfileImg} alt="Profile" className="profile-image" />
          </div>
          <div className="profile-username">{user?.name}</div>
          <div className="profile-email">
            <img src={EmailIcon} alt="Email" className="email-icon" />
            <span>{user?.email}</span>
          </div>
          <div className="profile-description">
            My negotiating strategy type is{" "}
            {profileData.type || "not set. Please update from profile"}.
          </div>
          <div className="profile-actions">
            <button
              className="logout-btn"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              Logout
            </button>
            <button
              className="view-profile-btn"
              onClick={() => {
                setIsDropdownOpen(false); // Close the dropdown menu
                navigate("/profile");
              }}
            >
              View Profile
            </button>
          </div>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};
